import { ErrorCard, OrganisationProductSummary } from "@fero/components";
import {
  useGetProductIdsQuery,
  useGetProductsQuery,
  useGetSavedProductPricesQuery,
  useSaveProductPrices,
  useSelectedOrgId,
} from "@fero/hooks";
import { useDefaultSalePricesStore } from "@fero/stores";
import { FeroCurrencyUtils } from "@fero/utils";
import type { ProductSalePriceInfo } from "@fero-shared/api-contracts";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import type { Product } from "adl-gen/ferovinum/app/db";
import {
  type ColumnDef,
  GridTable,
  GridTableColumnFactory,
} from "components/widgets/grid-table/grid-table";
import { CurrencyInput } from "components/widgets/inputs/currency-input/currency-input";
import type React from "react";
import { useMemo } from "react";
import { assertNotUndefined } from "utils/hx/util/types";
import { getVesselCapacity } from "utils/model-utils";
import { useDefaultSalePricesHelpers } from "../hooks";
import useCurrencySelect from "./currency-selector";
import { ImportPriceListCard } from "./import-price-list-card";

type ProductId = string;
export type ProductAggregate = {
  id?: string | number;
  productId: ProductId;
  product: Product;
  savedPrices: { [currency: string]: ProductSalePriceInfo };
};
type ProductAggregateMap = Map<ProductId, ProductAggregate>;

interface EditProductSalePriceButtonProps {
  productAggregate: ProductAggregate;
}
const EditProductSalePriceButton: React.FC<EditProductSalePriceButtonProps> = ({
  productAggregate,
}) => {
  const { currency } = useDefaultSalePricesStore();
  const { isEditingPrice, startEditingPrice, cancelEditingPrice } =
    useDefaultSalePricesHelpers();

  const defaultSalePriceInfo: ProductSalePriceInfo = {
    productId: productAggregate.productId,
    productCode: productAggregate.product.code,
    currency,
    exDutyAndVatPrice: "0",
    incDutyAndVatPrice: "0",
    incDutyExVatPrice: "0",
  };

  const onEdit = () => {
    startEditingPrice(
      productAggregate.savedPrices[currency] ?? defaultSalePriceInfo,
    );
  };

  const onCancelEdit = () => {
    cancelEditingPrice(productAggregate.productId);
  };

  if (isEditingPrice(productAggregate.productId)) {
    return (
      <IconButton onClick={onCancelEdit}>
        <CloseIcon />
      </IconButton>
    );
  }

  return (
    <IconButton onClick={onEdit}>
      <EditIcon />
    </IconButton>
  );
};

interface ProductSalePriceCellProps {
  productAggregate: ProductAggregate;
  salePriceType:
    | "incDutyExVatPrice"
    | "incDutyAndVatPrice"
    | "exDutyAndVatPrice";
}
const ProductSalePriceCell: React.FC<ProductSalePriceCellProps> = ({
  productAggregate,
  salePriceType,
}) => {
  const { currency, salePricesToSave } = useDefaultSalePricesStore();
  const { isEditingPrice, updatePriceToSave } = useDefaultSalePricesHelpers();

  if (isEditingPrice(productAggregate.productId)) {
    const priceToEdit = salePricesToSave.find(
      (p) =>
        p.currency === currency && p.productId === productAggregate.productId,
    );
    return (
      <CurrencyInput
        currency={currency}
        name={`${salePriceType}-${productAggregate.productId}`}
        value={priceToEdit ? (priceToEdit[salePriceType] ?? "0") : "0"}
        onChange={(e) =>
          updatePriceToSave(productAggregate.productId, {
            [salePriceType]: e.target.value,
          })
        }
      />
    );
  }

  const savedPrice: ProductSalePriceInfo | undefined =
    productAggregate.savedPrices[currency];
  return (
    <Typography>
      {savedPrice
        ? `${FeroCurrencyUtils.currencyCodeToSymbol(currency)}${savedPrice[salePriceType] ?? 0}`
        : "-"}
    </Typography>
  );
};

export const ProductsTable: React.FC = () => {
  const organisationId = assertNotUndefined(useSelectedOrgId());

  const { isEditingPrices, salePricesToSave, clearEditingPrices } =
    useDefaultSalePricesHelpers();
  const saveProductPricesMutation = useSaveProductPrices();
  const { currency, CurrencySelect, currencySelectorEnabled } =
    useCurrencySelect(organisationId);

  const {
    data: productIds = [],
    isLoading: isLoadingProductIds,
    isError: isErrorLoadingProductIds,
  } = useGetProductIdsQuery({ storageLocationId: null, finishType: null });
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    isError: isErrorLoadingProducts,
  } = useGetProductsQuery({ productIds, enabled: productIds.length > 0 });
  const {
    data: savedPrices,
    isLoading: isLoadingSavedPrices,
    isError: isErrorSavedPrices,
  } = useGetSavedProductPricesQuery({
    organisationId,
    purchaserId: null,
  });

  const onCancelEditSalePrices = () => {
    clearEditingPrices();
  };
  const onSubmitEditSalePrices = async () => {
    await saveProductPricesMutation.mutateAsync({
      body: { organisationId, purchaserId: null, salePrices: salePricesToSave },
    });
    clearEditingPrices();
  };

  const productInfoAggregateMap = useMemo(() => {
    const productMap: ProductAggregateMap = new Map();
    for (const product of products) {
      productMap.set(product.id, {
        productId: product.id,
        product: product.value,
        savedPrices: {},
      });
    }
    // If no saved prices loaded yet, we can just early exit
    if (!savedPrices) {
      return productMap;
    }

    for (const savedPrice of savedPrices.body) {
      const productId = savedPrice.productId;
      const productAggregate = productMap.get(productId);
      if (productAggregate && savedPrice.currency === currency) {
        productAggregate.savedPrices[savedPrice.currency] = savedPrice;
      }
    }
    return productMap;
  }, [products, savedPrices, currency]);

  const factory = new GridTableColumnFactory<ProductAggregate>();
  const COLUMNS: ColumnDef<ProductAggregate, string>[] = [
    factory.makeNodeColumn({
      header: "Product",
      key: "product",
      width: 200,
      renderNode: (row) => (
        <OrganisationProductSummary
          code={row.product.code}
          name={row.product.name}
          producerName={row.product.producerName}
          productDate={row.product.productDate}
          unitType={row.product.unitType}
          vesselCapacity={getVesselCapacity(row.product.vesselSize)}
        />
      ),
      getValue: (row) => `${row.product.code}`,
    }),
  ];

  if (savedPrices?.body.length) {
    COLUMNS.push(
      factory.makeNodeColumn({
        key: "incDutyExVatPrice",
        header: "inc. duty ex. VAT",
        flex: 0.25,
        align: "right",
        renderNode: (row) => (
          <ProductSalePriceCell
            productAggregate={row}
            salePriceType="incDutyExVatPrice"
          />
        ),
        getValue: (row) => {
          const { currency } = useDefaultSalePricesStore.getState();
          const savedPrice = row.savedPrices[currency];
          return savedPrice
            ? `${FeroCurrencyUtils.currencyCodeToSymbol(currency)}${savedPrice.incDutyExVatPrice ?? 0}`
            : "-";
        },
      }),
      factory.makeNodeColumn({
        key: "incDutyAndVatPrice",
        header: "inc. duty inc. VAT",
        flex: 0.25,
        align: "right",
        renderNode: (row) => (
          <ProductSalePriceCell
            productAggregate={row}
            salePriceType="incDutyAndVatPrice"
          />
        ),
        getValue: (row) => {
          const { currency } = useDefaultSalePricesStore.getState();
          const savedPrice = row.savedPrices[currency];
          return savedPrice
            ? `${FeroCurrencyUtils.currencyCodeToSymbol(currency)}${savedPrice.incDutyAndVatPrice ?? 0}`
            : "-";
        },
      }),
      factory.makeNodeColumn({
        key: "exDutyAndVatPrice",
        header: "ex. duty ex. VAT",
        flex: 0.25,
        align: "right",
        renderNode: (row) => (
          <ProductSalePriceCell
            productAggregate={row}
            salePriceType="exDutyAndVatPrice"
          />
        ),
        getValue: (row) => {
          const { currency } = useDefaultSalePricesStore.getState();
          const savedPrice = row.savedPrices[currency];
          return savedPrice
            ? `${FeroCurrencyUtils.currencyCodeToSymbol(currency)}${savedPrice.exDutyAndVatPrice ?? 0}`
            : "-";
        },
      }),
      factory.makeNodeColumn({
        key: "edit-product",
        header: "",
        flex: 0.1,
        hideFromCSVDownload: true,
        renderNode: (row) => (
          <EditProductSalePriceButton productAggregate={row} />
        ),
      }),
    );
  }

  if (isLoadingProductIds || isLoadingProducts || isLoadingSavedPrices) {
    return (
      <Box width={"100%"} height={800}>
        <Skeleton variant="rounded" animation="wave" height="100%" />
      </Box>
    );
  }

  if (
    isErrorLoadingProductIds ||
    isErrorLoadingProducts ||
    isErrorSavedPrices
  ) {
    return <ErrorCard />;
  }

  const orderedProductAggregateArray = Array.from(
    productInfoAggregateMap.values(),
  ).sort((a, b) => (a.product.name < b.product.name ? -1 : 1));
  const filters = [];
  if (currencySelectorEnabled) filters.push(CurrencySelect);
  return (
    <Stack spacing={4}>
      {savedPrices.body.length === 0 && <ImportPriceListCard />}
      <GridTable
        inputRowsField={orderedProductAggregateArray}
        columns={COLUMNS}
        tableHeight={800}
        searchField
        filters={filters}
        csvDownload
      />
      {isEditingPrices && (
        <Stack
          direction={"row"}
          spacing={2}
          justifyContent={"flex-end"}
          justifySelf={"flex-end"}
          alignItems={"center"}
        >
          <Typography>
            Editing {salePricesToSave.length} Product
            {salePricesToSave.length > 1 ? "s" : ""}
          </Typography>
          <Button
            variant="outlined"
            onClick={onCancelEditSalePrices}
            disabled={saveProductPricesMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={saveProductPricesMutation.isLoading}
            onClick={onSubmitEditSalePrices}
          >
            Save
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
};
